import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import LEDBlink from '../../dashboards/order-table-view/components/led-blink';
import LEDBlinkPopupHOC from '../../dashboards/order-table-view/hoc/led-blink-popup-hoc';
import { useStore } from '../../../contexts/store-provider';
import { formatMessage } from '../../../translations/format-message';
import CalibrationStatusEnum from '../../../constants/enums/calibration-status-enum';
import InspectionFormatEnum from '../../../constants/enums/inspection-format-enum';
import UsageStatusEnum from '../../../constants/enums/usage-status-enum';
import LoanRecordEnum from '../../../constants/enums/loan-record-enum';
import RoutesConstants from '../../../shared-components/routes/routes-constants';
import noDataImg from '../../../assets/Images/no-data-list.svg';
import { RefreshIcon } from '../../../shared-components/svgs/svgs';
import {
  getTenantIdFromLocalStorage,
  getToolBOMFactoryIdFromLocalStorage,
} from '../../../helpers/localstorage-helper';
import {
  ToolInspectionStatusOptions,
  ToolUsageStatusOptions,
} from '../../tool-management/option';

/**
 * This function is used to check if a tool's disposal date has arrived (today or earlier).
 * @param disposalDate: DisposalDate value from the row
 */
const isDisposed = (disposalDate?: string | null) => {
  if (!disposalDate) return false;
  const disposal = new Date(disposalDate);
  if (isNaN(disposal.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  disposal.setHours(0, 0, 0, 0);
  return disposal <= today;
};

const isCalibrationLapsed = (row: {
  CalibrationStatus?: number | null;
  CalibrationExpiryDate?: string | null;
}) => {
  if (row.CalibrationStatus === CalibrationStatusEnum.Expired) return true;
  if (!row.CalibrationExpiryDate) return false;
  const expiry = new Date(row.CalibrationExpiryDate);
  if (isNaN(expiry.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  expiry.setHours(0, 0, 0, 0);
  return expiry < today;
};

const isInspectionBlockedByLoan = (row: {
  LoanRecord?: number | null;
  LoanFactoryId?: number | null;
}) => {
  if (row.LoanRecord !== LoanRecordEnum.Yes) return false;
  const currentTenantId = Number(getTenantIdFromLocalStorage());
  return Number(row.LoanFactoryId) !== currentTenantId;
};

/**
 * This function is used to render a InspectionStatus/UsageStatus cell.
 * @param options: Static option list (id = enum value, value = translation key)
 * @param cell: Numeric status value to render
 */
const StatusLabel = ({
  options,
  cell,
}: {
  options: { id: number; value: string }[];
  cell?: number | null;
}) => {
  if (cell === null || cell === undefined) return <>-</>;
  const option = options.find((o) => o.id === cell);
  return option ? <FormattedMessage id={option.value} /> : <>-</>;
};

interface IProps {
  onInspectionOpen?: () => void;
  showLEDBlinkPopup?: boolean;
  addToggleHandler?: () => void;
}

/**
 * This functional component renders the "Target Tool List" accordion item.
 */
const TargetToolList = (props: IProps) => {
  const { onInspectionOpen } = props;
  const { toolBOMStore, dashboardStore } = useStore();
  const {
    toolBomToolManagementList,
    getToolBomToolManagementListState,
    selectedToolModel,
    GetToolBomToolManagementListService,
    scannedToolNumbers,
  } = toolBOMStore;
  const { ledBlinkState, resetLedBlinkState } = dashboardStore;

  const [selectedId, setSelectedId] = useState<string>('');
  // const [selectedIds, setSelectedIds] = useState<any[]>([]);

  useEffect(() => {
    if (ledBlinkState.success) {
      resetLedBlinkState();
    }
  }, [ledBlinkState.success]);

  useEffect(() => {
    if (ledBlinkState.error) {
      toast.error(formatMessage(ledBlinkState.error));
      resetLedBlinkState();
    }
  }, [ledBlinkState.error]);

  /**
   * This function is used to re-fetch the target tool list for whichever Material/Tool row's Get
   * button was last clicked, so the operator can pull the latest Inspection/Usage status without
   * having to go back to the left table and click Get again.
   */
  const refreshClickHandler = () => {
    if (!selectedToolModel) return;
    GetToolBomToolManagementListService(
      selectedToolModel,
      getToolBOMFactoryIdFromLocalStorage() || '',
      Number(getTenantIdFromLocalStorage())
    );
  };

  /**
   * This function is used to open the tool's inspection module in a new browser tab based on InspectionFormat.
   * @param toolId: Tool Identifier
   * @param inspectionFormat: InspectionFormatEnum value (Crimping Tool vs. User Created)
   */
  const inspectionClickHandler = (toolId: number, inspectionFormat: number) => {
    onInspectionOpen?.();
    const routePath =
      inspectionFormat === InspectionFormatEnum.CrimpingToolInspection
        ? RoutesConstants.CrimpToolInspection
        : RoutesConstants.UserCreatedInspection;
    window.open(routePath.replace(':toolId', String(toolId)), '_blank');
  };

  /**
   * This function is used to blink the tool's E-Tag.
   * @param toolId: Tool Identifier
   * @param toolManagementNo: Tool Management Number
   */
  const ledClickHandler = (toolManagementNo: string) => {
    setSelectedId(toolManagementNo);
    props.addToggleHandler?.();
  };

  return (
    <div className="tableDesign operator-tool-management-table">
      {props.showLEDBlinkPopup && (
        <LEDBlink
          id={0}
          modalClosed={props.addToggleHandler || (() => {})}
          selectedRows={[]}
          isToolLed={true}
          toolId={selectedId}
        />
      )}
      <div className="row my-2">
        <div className="col-6">
          <p className="small text-muted">
            {formatMessage('target_tool_list')}
          </p>
        </div>
        <div className="col-6 d-flex justify-content-end">
          <button
            type="button"
            className="btn btn-light btn-sm"
            title={formatMessage('refresh')}
            disabled={
              !selectedToolModel || getToolBomToolManagementListState.inProgress
            }
            onClick={refreshClickHandler}
          >
            <RefreshIcon />
          </button>
        </div>
      </div>

      <div className="table-responsive max-height-table">
        <div className="react-bootstrap-table">
          <table className="table table-hover table-bordered">
            <thead>
              <tr>
                <th>
                  <FormattedMessage id="tool_management_no" />
                </th>
                <th>
                  <FormattedMessage id="inspection_status" />
                </th>
                <th>
                  <FormattedMessage id="usage_status" />
                </th>
                <th>
                  <FormattedMessage id="inspection" />
                </th>
                <th>
                  <FormattedMessage id="led" />
                </th>
              </tr>
            </thead>
            <tbody>
              {toolBomToolManagementList && toolBomToolManagementList.length > 0
                ? toolBomToolManagementList.map((row, index) => {
                    // Inspection stays enabled regardless of InspectionStatus (an inspected tool can
                    // be inspected again). It is only blocked for an Unusable tool, a tool that
                    // is In Use but not scanned into the current tray (i.e. in use elsewhere), or a
                    // tool that is on loan to another factory.
                    const isCurrentlyScannedTool = scannedToolNumbers.includes(
                      row.ToolManagementNo
                    );
                    return (
                    <tr key={index}>
                      <td>{row.ToolManagementNo}</td>
                      <td>
                        <StatusLabel
                          options={ToolInspectionStatusOptions}
                          cell={row.InspectionStatus}
                        />
                      </td>
                      <td>
                        <StatusLabel
                          options={ToolUsageStatusOptions}
                          cell={row.UsageStatus}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          disabled={
                            (row.UsageStatus === UsageStatusEnum.InUse &&
                              !isCurrentlyScannedTool) ||
                            row.UsageStatus === UsageStatusEnum.Unusable ||
                            isDisposed(row.DisposalDate) ||
                            isCalibrationLapsed(row) ||
                            isInspectionBlockedByLoan(row)
                          }
                          onClick={() =>
                            inspectionClickHandler(
                              row.ToolId,
                              row.InspectionFormat
                            )
                          }
                        >
                          <FormattedMessage id="inspection" />
                        </button>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-warning btn-sm"
                          onClick={() =>
                            ledClickHandler(row.ToolManagementNo)
                          }
                        >
                          <FormattedMessage id="led" />
                        </button>
                      </td>
                    </tr>
                    );
                  })
                : !getToolBomToolManagementListState.inProgress && (
                    <tr>
                      <td
                        colSpan={5}
                        className="text-center"
                      >
                        <div className="no-data-img text-center py-3">
                          <img
                            src={noDataImg}
                            alt={formatMessage('no_tool_management_data')}
                            style={{ maxWidth: 160 }}
                          />
                          <p className="mb-0">
                            <FormattedMessage id="no_tool_management_data" />
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LEDBlinkPopupHOC(observer(TargetToolList));
